import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';

interface Member {
  id: string;
  user_id: string;
  name?: string;
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role: string;
}

interface Attendance {
  userId: string;
  isPresent: boolean;
  attendanceType: 'physical';
}

interface MeetingMinutes {
  content: string;
  status: string;
  takenBy?: string;
  takenByName?: string;
}

interface Document {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: string;
  url?: string;
}

const PhysicalMeeting: React.FC = () => {
  const colors = getThemeColors('dark');

  const [loading, setLoading] = useState(false);
  const [chamaMembers, setChamaMembers] = useState<Member[]>([]);
  const [memberAttendance, setMemberAttendance] = useState<{[key: string]: boolean}>({});
  const [notes, setNotes] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState<Document[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [previousMeeting, setPreviousMeeting] = useState<any>(null);
  const [previousMinutes, setPreviousMinutes] = useState<MeetingMinutes | null>(null);
  const [showPrevMinutes, setShowPrevMinutes] = useState(false);

  // Mock data for demonstration
  useEffect(() => {
    loadChamaMembers();
    loadPreviousMeetingData();
  }, []);

  const loadChamaMembers = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const mockMembers: Member[] = [
        { id: '1', user_id: '1', first_name: 'John', last_name: 'Doe', email: 'john@example.com', role: 'chairperson' },
        { id: '2', user_id: '2', first_name: 'Jane', last_name: 'Smith', email: 'jane@example.com', role: 'secretary' },
        { id: '3', user_id: '3', first_name: 'Bob', last_name: 'Johnson', email: 'bob@example.com', role: 'treasurer' },
        { id: '4', user_id: '4', first_name: 'Alice', last_name: 'Brown', email: 'alice@example.com', role: 'member' },
        { id: '5', user_id: '5', first_name: 'Charlie', last_name: 'Wilson', email: 'charlie@example.com', role: 'member' },
      ];

      setChamaMembers(mockMembers);

      // Initialize attendance tracking
      const initialAttendance: {[key: string]: boolean} = {};
      mockMembers.forEach(member => {
        initialAttendance[member.user_id] = false;
      });
      setMemberAttendance(initialAttendance);
    } catch (error) {
      console.error('Failed to load chama members:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPreviousMeetingData = async () => {
    try {
      // Simulate loading previous meeting
      await new Promise(resolve => setTimeout(resolve, 500));

      const mockPreviousMeeting = {
        id: 'prev-1',
        title: 'Previous Monthly Meeting',
        scheduledAt: '2024-12-15T14:00:00+03:00',
      };

      const mockPreviousMinutes: MeetingMinutes = {
        content: 'Previous meeting minutes content...',
        status: 'approved',
        takenBy: 'secretary1',
        takenByName: 'Jane Smith'
      };

      setPreviousMeeting(mockPreviousMeeting);
      setPreviousMinutes(mockPreviousMinutes);
    } catch (error) {
      console.error('Failed to load previous meeting data:', error);
    }
  };

  const toggleMemberAttendance = async (userId: string) => {
    try {
      const currentStatus = memberAttendance[userId] || false;
      const newStatus = !currentStatus;

      // Update local state immediately
      setMemberAttendance(prev => ({
        ...prev,
        [userId]: newStatus
      }));

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));

      alert(`Attendance updated: ${newStatus ? 'Present' : 'Absent'}`);
    } catch (error) {
      console.error('Failed to update attendance:', error);
      // Revert on error
      setMemberAttendance(prev => ({
        ...prev,
        [userId]: !memberAttendance[userId]
      }));
    }
  };

  const saveMeetingData = async () => {
    try {
      setIsSaving(true);

      // Get attendance data
      const presentMembers = Object.entries(memberAttendance)
        .filter(([, isPresent]) => isPresent)
        .map(([userId]) => userId);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      alert(`Meeting saved! ${presentMembers.length} members marked present.`);
    } catch (error) {
      console.error('Failed to save meeting data:', error);
      alert('Failed to save meeting data. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const instantSaveAndExit = async () => {
    await saveMeetingData();
    alert('Meeting saved and exited successfully!');
    // Navigate back
  };

  const uploadDocument = () => {
    // Simulate file upload
    const mockDocument: Document = {
      id: Date.now().toString(),
      name: 'Meeting Document.pdf',
      size: 1024000,
      type: 'application/pdf',
      uploadedAt: new Date().toISOString(),
    };

    setUploadedDocuments(prev => [...prev, mockDocument]);
    alert('Document uploaded successfully!');
  };

  const removeDocument = (docId: string) => {
    setUploadedDocuments(prev => prev.filter(doc => doc.id !== docId));
    alert('Document removed successfully!');
  };

  const saveNotes = async () => {
    if (!notes.trim()) {
      alert('Please enter some notes before saving');
      return;
    }

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('Notes saved successfully!');
    } catch (error) {
      console.error('Failed to save notes:', error);
      alert('Failed to save notes. Please try again.');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getAttendanceStats = () => {
    const present = Object.values(memberAttendance).filter(Boolean).length;
    const absent = chamaMembers.length - present;
    return { present, absent, total: chamaMembers.length };
  };

  const canMarkAttendance = true; // Mock permission
  const canTakeNotes = true; // Mock permission
  const canEndMeeting = true; // Mock permission
  const canUploadDocuments = true; // Mock permission

  const stats = getAttendanceStats();

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    header: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.sm,
    },
    headerTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
    },
    backButton: {
      padding: spacing.sm,
      cursor: 'pointer',
      color: colors.text,
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    section: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('sm'),
    },
    sectionTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.md,
    },
    prevHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.sm,
    },
    prevMeta: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginBottom: spacing.sm,
    },
    prevToggle: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.md,
      cursor: 'pointer',
      alignSelf: 'flex-start',
    },
    prevToggleText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      color: colors.text,
    },
    prevContent: {
      marginTop: spacing.md,
    },
    prevStatus: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginBottom: spacing.sm,
    },
    prevBody: {
      fontSize: typography.fontSize.sm,
      color: colors.text,
      lineHeight: 1.5,
      marginBottom: spacing.md,
    },
    approveButton: {
      alignSelf: 'flex-start',
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
      borderRadius: borderRadius.md,
      cursor: 'pointer',
    },
    approveButtonText: {
      color: 'white',
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
    },
    attendanceHeader: {
      marginBottom: spacing.md,
    },
    attendanceSubtitle: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginTop: spacing.xs,
    },
    membersList: {
      gap: spacing.sm,
    },
    memberItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: spacing.md,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    memberItemPresent: {
      backgroundColor: colors.success + '15',
      borderColor: colors.success,
      boxShadow: `0 0 0 2px ${colors.success}20`,
    },
    memberInfo: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
    },
    memberAvatar: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: colors.primary + '20',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.md,
      flexShrink: 0,
    },
    memberAvatarText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.bold,
      color: colors.primary,
    },
    memberDetails: {
      flex: 1,
    },
    memberName: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.text,
      marginBottom: spacing.xs / 2,
    },
    memberRole: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
    },
    attendanceStatus: {
      display: 'flex',
      alignItems: 'center',
    },
    presentIndicator: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
      paddingTop: spacing.xs,
      paddingBottom: spacing.xs,
      borderRadius: borderRadius.sm,
    },
    presentText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      marginLeft: spacing.xs,
    },
    absentIndicator: {
      display: 'flex',
      alignItems: 'center',
    },
    absentText: {
      fontSize: typography.fontSize.sm,
      marginLeft: spacing.xs,
    },
    tapHint: {
      fontSize: typography.fontSize.xs,
      fontStyle: 'italic',
      marginLeft: spacing.xs,
      marginTop: spacing.xs / 2,
    },
    attendanceSummary: {
      display: 'flex',
      justifyContent: 'space-around',
      padding: spacing.lg,
      borderRadius: borderRadius.md,
      backgroundColor: colors.backgroundSecondary,
      marginTop: spacing.md,
    },
    summaryItem: {
      textAlign: 'center',
    },
    summaryLabel: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginBottom: spacing.xs / 2,
    },
    summaryValue: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
    },
    notesInput: {
      width: '100%',
      padding: spacing.md,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.background,
      color: colors.text,
      fontSize: typography.fontSize.base,
      minHeight: 120,
      outline: 'none',
      resize: 'vertical',
    },
    saveNotesButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.md,
      borderRadius: borderRadius.md,
      backgroundColor: colors.primary,
      color: colors.white,
      border: 'none',
      cursor: 'pointer',
      marginTop: spacing.md,
      gap: spacing.sm,
    },
    saveNotesText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
    },
    uploadButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.lg,
      borderRadius: borderRadius.md,
      border: `2px dashed ${colors.primary}`,
      backgroundColor: colors.backgroundSecondary,
      cursor: 'pointer',
      gap: spacing.sm,
    },
    uploadButtonText: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.primary,
    },
    documentsList: {
      marginTop: spacing.md,
    },
    documentItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      borderBottom: `1px solid ${colors.border}`,
    },
    documentInfo: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
    },
    documentDetails: {
      marginLeft: spacing.md,
      flex: 1,
    },
    documentName: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      color: colors.text,
      marginBottom: spacing.xs / 2,
    },
    documentSize: {
      fontSize: typography.fontSize.xs,
      color: colors.textSecondary,
    },
    removeDocButton: {
      padding: spacing.sm,
      cursor: 'pointer',
      color: colors.error,
    },
    actionButtons: {
      display: 'flex',
      gap: spacing.sm,
      padding: spacing.md,
      borderTop: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
    },
    actionButton: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
      borderRadius: borderRadius.sm,
      cursor: 'pointer',
      gap: spacing.xs,
      minHeight: 40,
    },
    saveButton: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    instantSaveButton: {
      backgroundColor: colors.success,
      color: colors.white,
    },
    startMeetingButton: {
      backgroundColor: colors.warning,
      color: 'white',
    },
    endMeetingButton: {
      backgroundColor: colors.error,
      color: 'white',
    },
    actionButtonText: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.medium,
      textAlign: 'center',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xxxl,
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: spacing.xl,
      paddingBottom: spacing.xl,
    },
    emptyIcon: {
      fontSize: 48,
      marginBottom: spacing.lg,
      opacity: 0.5,
    },
    emptyText: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      textAlign: 'center',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={{ fontSize: 48, marginBottom: spacing.lg }}>⏳</div>
          <div style={{ fontSize: typography.fontSize.lg, color: colors.text }}>
            Loading meeting details...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <button
            style={styles.backButton}
            onClick={() => alert('Navigate back to meetings')}
          >
            ←
          </button>
          <h1 style={styles.headerTitle}>Physical Meeting</h1>
        </div>
        <div style={{ fontSize: typography.fontSize.base, color: colors.textSecondary }}>
          Monthly Chama Meeting
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          marginTop: spacing.sm
        }}>
          <span style={{
            paddingLeft: spacing.sm,
            paddingRight: spacing.sm,
            paddingTop: spacing.xs,
            paddingBottom: spacing.xs,
            borderRadius: borderRadius.sm,
            backgroundColor: colors.primary + '20',
            color: colors.primary,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
          }}>
            📍 Physical Meeting
          </span>
        </div>
        <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginTop: spacing.sm }}>
          📍 Community Hall, Westlands
        </div>
      </div>

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
        {/* Previous Meeting Minutes Section */}
        <div style={styles.section}>
          <div style={styles.prevHeader}>
            <h2 style={styles.sectionTitle}>Previous Meeting Minutes</h2>
          </div>
          {previousMeeting ? (
            <>
              <div style={styles.prevMeta}>
                Last meeting: {new Date(previousMeeting.scheduledAt).toLocaleString()}
              </div>
              {previousMinutes ? (
                <>
                  <button
                    style={styles.prevToggle}
                    onClick={() => setShowPrevMinutes(!showPrevMinutes)}
                  >
                    <span>{showPrevMinutes ? '▼' : '▶'}</span>
                    <span style={styles.prevToggleText}>View last minutes</span>
                  </button>

                  {showPrevMinutes && (
                    <div style={styles.prevContent}>
                      <div style={styles.prevStatus}>
                        Status: {previousMinutes.status?.toUpperCase()}
                      </div>
                      <div style={styles.prevBody}>
                        {previousMinutes.content}
                      </div>
                      <button
                        style={{ ...styles.approveButton, backgroundColor: colors.success }}
                        onClick={() => alert('Minutes approved!')}
                      >
                        <span>✓</span>
                        <span style={styles.approveButtonText}>Approve Minutes</span>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div style={styles.prevMeta}>No minutes found for the previous meeting.</div>
              )}
            </>
          ) : (
            <div style={styles.prevMeta}>No previous meeting found.</div>
          )}
        </div>

        {/* Attendance Section */}
        <div style={styles.section}>
          <div style={styles.attendanceHeader}>
            <h2 style={styles.sectionTitle}>Meeting Attendance</h2>
            {canMarkAttendance && (
              <div style={styles.attendanceSubtitle}>
                Mark members as present by tapping their names
              </div>
            )}
          </div>

          {/* Member List for Attendance */}
          {chamaMembers.length > 0 ? (
            <div style={styles.membersList}>
              {chamaMembers.map((member) => {
                const userId = member.user_id;
                const firstName = member.first_name || '';
                const lastName = member.last_name || '';
                const fullName = `${firstName} ${lastName}`.trim();
                const displayName = fullName || member.name || member.username || member.email?.split('@')[0] || `Member ${userId}`;
                const isPresent = memberAttendance[userId] || false;

                return (
                  <div
                    key={userId}
                    style={{
                      ...styles.memberItem,
                      ...(isPresent ? styles.memberItemPresent : {}),
                    }}
                    onClick={() => canMarkAttendance && toggleMemberAttendance(userId)}
                  >
                    <div style={styles.memberInfo}>
                      <div style={styles.memberAvatar}>
                        <span style={styles.memberAvatarText}>
                          {firstName?.[0]?.toUpperCase() || 'M'}{lastName?.[0]?.toUpperCase() || ''}
                        </span>
                      </div>
                      <div style={styles.memberDetails}>
                        <div style={styles.memberName}>{displayName}</div>
                        <div style={styles.memberRole}>{member.role}</div>
                      </div>
                    </div>

                    <div style={styles.attendanceStatus}>
                      {isPresent ? (
                        <div style={{
                          ...styles.presentIndicator,
                          backgroundColor: colors.success + '20'
                        }}>
                          <span style={{ color: colors.success, fontSize: 16 }}>✓</span>
                          <span style={{ ...styles.presentText, color: colors.success }}>
                            Present
                          </span>
                        </div>
                      ) : (
                        <div style={styles.absentIndicator}>
                          <span style={{ color: colors.textSecondary, fontSize: 16 }}>○</span>
                          <div>
                            <div style={{ ...styles.absentText, color: colors.textSecondary }}>
                              Absent
                            </div>
                            {canMarkAttendance && (
                              <div style={{ ...styles.tapHint, color: colors.primary }}>
                                Tap to mark present
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>👥</div>
              <p style={styles.emptyText}>Loading chama members...</p>
            </div>
          )}

          {/* Attendance Summary */}
          {chamaMembers.length > 0 && (
            <div style={styles.attendanceSummary}>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Total Members:</div>
                <div style={{ ...styles.summaryValue, color: colors.text }}>{stats.total}</div>
              </div>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Present:</div>
                <div style={{ ...styles.summaryValue, color: colors.success }}>{stats.present}</div>
              </div>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Absent:</div>
                <div style={{ ...styles.summaryValue, color: colors.error }}>{stats.absent}</div>
              </div>
            </div>
          )}
        </div>

        {/* Notes Section */}
        {canTakeNotes && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Meeting Notes</h2>

            <textarea
              style={styles.notesInput}
              placeholder="Enter meeting notes, decisions, and action items..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={6}
            />

            <button
              style={styles.saveNotesButton}
              onClick={saveNotes}
            >
              <span>💾</span>
              <span style={styles.saveNotesText}>Save Notes</span>
            </button>
          </div>
        )}

        {/* Documents Section */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Documents</h2>

          <button
            style={styles.uploadButton}
            onClick={uploadDocument}
          >
            <span style={{ fontSize: 24 }}>☁️</span>
            <span style={styles.uploadButtonText}>Upload Document</span>
          </button>

          {uploadedDocuments.length > 0 && (
            <div style={styles.documentsList}>
              {uploadedDocuments.map((doc) => (
                <div key={doc.id} style={styles.documentItem}>
                  <div style={styles.documentInfo}>
                    <span style={{ fontSize: 20 }}>📄</span>
                    <div style={styles.documentDetails}>
                      <div style={styles.documentName}>{doc.name}</div>
                      <div style={styles.documentSize}>
                        {doc.type.split('/')[1]?.toUpperCase()} • {formatFileSize(doc.size)}
                      </div>
                    </div>
                  </div>
                  <button
                    style={styles.removeDocButton}
                    onClick={() => removeDocument(doc.id)}
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={styles.actionButtons}>
        {canMarkAttendance && (
          <button
            style={{ ...styles.actionButton, ...styles.saveButton }}
            onClick={saveMeetingData}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span>⏳</span>
                <span style={styles.actionButtonText}>
                  {isSaving ? 'Saving...' : 'Save'}
                </span>
              </>
            ) : (
              <>
                <span>💾</span>
                <span style={styles.actionButtonText}>Save</span>
              </>
            )}
          </button>
        )}

        {canMarkAttendance && (
          <button
            style={{ ...styles.actionButton, ...styles.instantSaveButton }}
            onClick={instantSaveAndExit}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span>⏳</span>
                <span style={styles.actionButtonText}>
                  {isSaving ? 'Saving...' : 'Save & Exit'}
                </span>
              </>
            ) : (
              <>
                <span>✓</span>
                <span style={styles.actionButtonText}>Save & Exit</span>
              </>
            )}
          </button>
        )}

        {canEndMeeting && (
          <button
            style={{ ...styles.actionButton, ...styles.startMeetingButton }}
            onClick={() => alert('Meeting started!')}
          >
            <span>▶</span>
            <span style={styles.actionButtonText}>Start Meeting</span>
          </button>
        )}

        {canEndMeeting && (
          <button
            style={{ ...styles.actionButton, ...styles.endMeetingButton }}
            onClick={() => alert('Meeting ended!')}
          >
            <span>⏹</span>
            <span style={styles.actionButtonText}>End Meeting Now</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default PhysicalMeeting;